/**
 * HelicopterPhysics - Core flight dynamics system
 * Manages collective, cyclic, yaw controls and physics simulation
 */

export interface PhysicsState {
  position: { x: number; y: number; z: number };
  velocity: { x: number; y: number; z: number };
  rotation: { pitch: number; roll: number; yaw: number };
  angularVelocity: { pitch: number; roll: number; yaw: number };
  collective: number; // 0-1, blade pitch
  torque: number; // 0-110%, engine load
}

export interface PhysicsConfig {
  mass: number; // kg
  maxCollective: number;
  maxTorque: number;
  dragCoefficient: number;
  groundEffectHeight: number; // meters
  groundEffectMultiplier: number;
}

export class HelicopterPhysics {
  private state: PhysicsState;
  private config: PhysicsConfig;
  private readonly GRAVITY = 9.81; // m/s^2

  constructor(config: Partial<PhysicsConfig> = {}) {
    this.config = {
      mass: 7600, // AH-64D approximate mass in kg
      maxCollective: 1.0,
      maxTorque: 110,
      dragCoefficient: 0.5,
      groundEffectHeight: 15.24, // 50 feet in meters
      groundEffectMultiplier: 1.2,
      ...config,
    };

    this.state = {
      position: { x: 0, y: 100, z: 0 }, // Start at 100m altitude
      velocity: { x: 0, y: 0, z: 0 },
      rotation: { pitch: 0, roll: 0, yaw: 0 },
      angularVelocity: { pitch: 0, roll: 0, yaw: 0 },
      collective: 0.5, // Start at hover collective
      torque: 0,
    };
  }

  /**
   * Update physics simulation
   * @param deltaTime Time elapsed in seconds
   * @param controls Input controls
   */
  update(
    deltaTime: number,
    controls: {
      collective: number; // -1 to 1
      cyclicPitch: number; // -1 to 1
      cyclicRoll: number; // -1 to 1
      yaw: number; // -1 to 1
    }
  ): void {
    // Update collective (blade pitch)
    this.state.collective = Math.max(
      0,
      Math.min(this.config.maxCollective, this.state.collective + controls.collective * deltaTime * 0.5)
    );

    // Calculate lift force
    const liftForce = this.calculateLift();

    // Calculate ground effect
    const groundEffectMultiplier = this.calculateGroundEffect();
    const totalLift = liftForce * groundEffectMultiplier;

    // Calculate drag
    const dragForce = this.calculateDrag();

    // Calculate net forces
    const netForceY = totalLift - this.config.mass * this.GRAVITY - dragForce.y;
    const netForceX = -dragForce.x;
    const netForceZ = -dragForce.z;

    // Update velocity (F = ma => a = F/m)
    this.state.velocity.y += (netForceY / this.config.mass) * deltaTime;
    this.state.velocity.x += (netForceX / this.config.mass) * deltaTime;
    this.state.velocity.z += (netForceZ / this.config.mass) * deltaTime;

    // Update angular velocities from cyclic controls
    const pitchAccel = controls.cyclicPitch * 0.5; // rad/s^2
    const rollAccel = controls.cyclicRoll * 0.5;
    const yawAccel = controls.yaw * 0.3;

    this.state.angularVelocity.pitch += pitchAccel * deltaTime;
    this.state.angularVelocity.roll += rollAccel * deltaTime;
    this.state.angularVelocity.yaw += yawAccel * deltaTime;

    // Apply angular damping
    this.state.angularVelocity.pitch *= 0.95;
    this.state.angularVelocity.roll *= 0.95;
    this.state.angularVelocity.yaw *= 0.98;

    // Update rotation
    this.state.rotation.pitch += this.state.angularVelocity.pitch * deltaTime;
    this.state.rotation.roll += this.state.angularVelocity.roll * deltaTime;
    this.state.rotation.yaw += this.state.angularVelocity.yaw * deltaTime;

    // Clamp pitch and roll
    this.state.rotation.pitch = Math.max(-Math.PI / 4, Math.min(Math.PI / 4, this.state.rotation.pitch));
    this.state.rotation.roll = Math.max(-Math.PI / 4, Math.min(Math.PI / 4, this.state.rotation.roll));

    // Apply velocity to rotation (cyclic affects velocity direction)
    const forwardVelocity = Math.sin(this.state.rotation.pitch) * 20;
    const lateralVelocity = Math.sin(this.state.rotation.roll) * 15;

    this.state.velocity.z += forwardVelocity * deltaTime;
    this.state.velocity.x += lateralVelocity * deltaTime;

    // Update position
    this.state.position.x += this.state.velocity.x * deltaTime;
    this.state.position.y += this.state.velocity.y * deltaTime;
    this.state.position.z += this.state.velocity.z * deltaTime;

    // Ground collision
    if (this.state.position.y < 0) {
      this.state.position.y = 0;
      this.state.velocity.y = Math.max(0, this.state.velocity.y);
    }

    // Update torque (related to collective)
    this.state.torque = Math.min(this.config.maxTorque, this.state.collective * 80 + 20);
  }

  private calculateLift(): number {
    // Simplified lift calculation based on collective
    // L = k * collective * rho * A * v^2
    const baseLift = this.state.collective * this.config.mass * this.GRAVITY * 2;
    return baseLift;
  }

  private calculateDrag(): { x: number; y: number; z: number } {
    const dragX = this.state.velocity.x * Math.abs(this.state.velocity.x) * this.config.dragCoefficient;
    const dragY = this.state.velocity.y * Math.abs(this.state.velocity.y) * this.config.dragCoefficient * 0.5;
    const dragZ = this.state.velocity.z * Math.abs(this.state.velocity.z) * this.config.dragCoefficient;

    return { x: dragX, y: dragY, z: dragZ };
  }

  private calculateGroundEffect(): number {
    if (this.state.position.y > this.config.groundEffectHeight) {
      return 1.0;
    }

    // Linear interpolation of ground effect
    const factor = this.state.position.y / this.config.groundEffectHeight;
    return 1.0 + (this.config.groundEffectMultiplier - 1.0) * (1.0 - factor);
  }

  getState(): Readonly<PhysicsState> {
    return { ...this.state };
  }

  setState(newState: Partial<PhysicsState>): void {
    this.state = { ...this.state, ...newState };
  }

  getAltitudeAGL(): number {
    return Math.max(0, this.state.position.y);
  }
}
